<template>
  <div class="widget-container">
    <div class="content">
      <div class="flex-row">
        <dashboard-widget-header :title="config.name"></dashboard-widget-header>
        <router-link :to="{ name: 'progress', query: queryString }" class="router-link">
          View All
        </router-link>
      </div>
      <div>
        <div v-for="cat in tallies" :key="cat.id" class="category-wrapper">
          <span class="flex-row" :data-test="`progress-bar-cat-${cat.id}`">
            <span class="category-name" data-test="category-name">
              {{ cat.name }}
            </span>
            <span class="category-percent-correct" data-test="category-percent-correct">
              {{ cat.percentCorrect }}%
            </span>
          </span>
          <widget-progress-bar
            :count="cat.count"
            :red="cat.red"
            :yellow="cat.yellow"
            :green="cat.green"
          ></widget-progress-bar>
        </div>
      </div>
      <div class="button-link">
        <router-link :to="{ name: 'progress', query: queryString }" class="router-link">
          View All
        </router-link>
      </div>
    </div>
    <div class="obfuscator" :class="{ revealed: hasPaidProgress }">
      <div class="obfuscator-content">
        <div class="icon-box">
          <icon name="lock-uui" variant="364152"></icon>
        </div>
        <h3>Subscribe to Unlock</h3>
        <p>Performance by category requires a subscription.</p>
        <button
          class="subscribe-button"
          @click="showUpSellModal"
        >
          Subscribe Now
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import WidgetProgressBar from './WidgetProgressBar';
import Icon from '@components/singletons/icon';
import DashboardWidgetHeader from '@components/dashboard-widgets/header';
import PerformanceMixin from '@/mixins/performance';
import CategoriesMixin from '@/mixins/categories';
import { PAID_FEATURES } from '@/utils/acl';

export default {
  components: { DashboardWidgetHeader, WidgetProgressBar, Icon },
  mixins: [PerformanceMixin, CategoriesMixin],
  computed: {
    accessibleCategories() {
      return this.categoryIds.map(id => this.allCategories[id])
        .filter(category => this.$access(this.$store, 'category', category.id));
    },
    allCategories() {
      return this.$store.getters['categories/categories'];
    },
    hasPaidProgress() {
      return this.$access(this.$store, 'feature', PAID_FEATURES.PROGRESS);;
    },
    parentCategoryIds() {
      return this.accessibleCategories.filter(category => !category.parentCategoryId).map(category => category.id);
    },
    queryString() {
      if (this.resourceType == 'categoryType') {
        return { categoryTypeId: this.resourceId };
      } else if (this.resourceType == 'grouping') {
        return { groupingId: this.resourceId };
      } else {
        return {};
      }
    },
    tallies() {
      const totals = [];
      const greenFlashcardStatus = this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
      const redFlashcardStatus = this.$store.getters['flashcardStatuses/redFlashcardStatus'];
      const yellowFlashcardStatus = this.$store.getters['flashcardStatuses/yellowFlashcardStatus'];

      for (let id of this.parentCategoryIds) {
        const data = {};
        const category = this.allCategories[id];
        const ids = [id, ...category.descendants];
        const leafs = this.$store.getters['categories/all'].filter(c => ids.includes(c.id) && c.children.length < 1);
        const states = this.flashcardStateList.filter(s => leafs.map(c => c.id).includes(s.categoryId));

        data['count'] = leafs.map(c => c.counts.total).reduce((a, b) => a + b, 0);
        data['correct'] = states.filter(s => s.correct).length;
        data['green'] = states.filter(s => s.flashcardStatusId == greenFlashcardStatus.id).length;
        data['yellow'] = states.filter(s => s.flashcardStatusId == yellowFlashcardStatus.id).length;
        data['red'] = states.filter(s => s.flashcardStatusId == redFlashcardStatus.id).length;
        data['answered'] = data['green'] + data['yellow'] + data['red'];
        data['id'] = category.id;
        data['name'] = category.name;
        data['percentCorrect'] = Math.round(data['correct'] / data['answered'] * 100) || 0;

        totals.push(data);
      }
      return totals
        .filter(cat => cat.count > 0)
        .sort((a, b) => b.percentCorrect - a.percentCorrect)
        .slice(0, 4);
    },
  },
  methods: {
    showUpSellModal() {
      const params = {
        show: true,
        source: {
          type: 'Locked Progress Modal',
        },
        resource: {
          type: 'progress',
          id: 'navigationID',
        },
      };
      this.$store.dispatch('ui/toggleUpsellModal', params);
    },
  },
};
</script>

<style scoped lang="scss">
.widget-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;

  .content {
    padding: 1.5rem;
    border: 1px solid #f1f1f1;
    border-radius: 0.5rem;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }

  .obfuscator {
    padding: 1.5rem;
    display: grid;
    justify-content: center;
    align-items: center;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
    background-color: rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(4px);

    &.revealed {
      display: none;
    }

    .obfuscator-content {
      padding: 6rem 2rem;
      text-align: center;
      z-index: 1;
      position: relative;

      ::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background-image: url('../../assets/images/dot-grid.svg');
        background-size: cover;
        -webkit-mask:radial-gradient(circle at 50% 50%, white, transparent 65%);
          mask:radial-gradient(circle at 50% 50%, white, transparent 65%);
        z-index: -1;
      }

      .icon-box {
        width: 2.25rem;
        height: 2.25rem;
        margin: 0 auto;
        display: grid;
        place-content: center;
        background-color: #fff;
        border: 1px solid #CDD5DF;
        border-radius: 0.5rem;
      }

      h3 {
        font-size: rem-calc(20);
        font-weight: 600;
      }

      p {
        font-size: rem-calc(16);
        font-weight: 400;
      }

      .subscribe-button {
        background-color: #326FF0;
        padding: 0.75rem 1.25rem;
        border-radius: .5rem;
        font-size: rem-calc(16);
        font-weight: 600;
        color: #fff;
      }
    }
  }
}

.flex-row {
  display: flex;
  justify-content: space-between;
  align-items: center;

  * {
    margin-bottom: 1rem;
  }
}

.router-link {
  font-size: rem-calc(17);
  font-weight: 600;
  color: #326ff0
}

.button-link {
  display: none;
}


@media screen and (max-width: 768px) {
  .router-link {
    display: none;
  }

  .button-link {
    display: grid;
    justify-content: center;
    background-color: #326FF0;
    padding: 0.75rem 0;
    border-radius: .5rem;

    .router-link {
      display: inline-block;
      color: #fff;
    }
  }
}

@media (min-width: 1201px) {
  .widget-container .content {
    height: rem-calc(518);
    padding: 2rem 1.5rem 1.5rem;
  }

  .router-link {
    display: none;
  }

  .button-link {
    margin-top: 4rem;
    display: grid;
    justify-content: center;
    background-color: #326FF0;
    padding: 0.75rem 0;
    border-radius: .5rem;

    .router-link {
      display: inline-block;
      color: #fff;
    }
  }
}

.category-name {
  font-size: rem-calc(17);
  font-weight: 400;
  color: #232323;
}

.category-percent-answered {
  font-size: rem-calc(14);
  font-weight: 400;
  color: #232323;
}

.category-wrapper {
  border-top: 1px solid rgba(153, 164, 174, 0.2);
  padding-top: 0.9rem;
  margin-top: 1rem;
  margin-bottom: 2rem;
}
</style>
